import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { displayError } from '../../helpers';
import { getCurrentDate } from '../../utils/DateUtils';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllEquipments } from '../../services/EquipmentServices';
import {
    createMaintenanceDaily,
    getMaintenanceAutofill,
} from '../../services/MaintenanceServices';
import MaintenanceAssetSections from './MaintenanceAssetSections';
import {
    buildDefaultDetailList,
    flattenGroupedLines,
    groupLinesByCategory,
    mergeAutofillSuggestions,
} from './maintenanceConstants';

function DailyPlantLog() {
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [equipments, setEquipments] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [inputs, setInputs] = useState({
        plant_id: '',
        log_date: getCurrentDate(),
        prepared_by: '',
        user_remarks: '',
    });
    const [groupedLines, setGroupedLines] = useState(() => groupLinesByCategory(buildDefaultDetailList()));

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || [])
                .filter((p) => p.status)
                .map((p) => ({ value: p.id, label: p.plant_alias })));
        });
        getAllEquipments(cookies).then((response) => {
            const list = response.equipment_list || [];
            setEquipments(list.filter((e) => e.status === true || e.status === 1 || e.status === 'Active'));
        });
    }, [cookies]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
    };

    const handleAutofill = () => {
        if (!inputs.plant_id || !inputs.log_date) {
            Swal.fire('Select plant and date before autofill.');
            return;
        }
        setInProgress(true);
        const displayDate = moment(inputs.log_date, 'YYYY-MM-DD').format('DD-MM-YYYY');
        getMaintenanceAutofill(cookies, inputs.plant_id, displayDate)
            .then((response) => {
                const currentFlat = flattenGroupedLines(groupedLines);
                const merged = mergeAutofillSuggestions(currentFlat, response.suggestions || []);
                setGroupedLines(groupLinesByCategory(merged));
                Swal.fire({ icon: 'success', title: 'Autofill applied', timer: 1200, showConfirmButton: false });
            })
            .catch((error) => displayError(error?.response?.data || error, 'Autofill Failed'))
            .finally(() => setInProgress(false));
    };

    const payload = useMemo(() => ({
        plant_id: inputs.plant_id,
        log_date: moment(inputs.log_date, 'YYYY-MM-DD').format('DD-MM-YYYY'),
        prepared_by: inputs.prepared_by,
        user_remarks: inputs.user_remarks,
        detail_list: flattenGroupedLines(groupedLines),
    }), [groupedLines, inputs]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!inputs.plant_id) {
            Swal.fire('Plant is required.');
            return;
        }
        if (!payload.detail_list.length) {
            Swal.fire('Enter at least one asset line.');
            return;
        }
        setInProgress(true);
        createMaintenanceDaily(cookies, payload)
            .then((response) => {
                Swal.fire({ icon: 'success', title: 'Daily plant log saved' }).then(() => {
                    navigate(`/DailyPlantLogEdit/${response.id}`);
                });
            })
            .catch((error) => displayError(error?.response?.data || error, 'Save Failed'))
            .finally(() => setInProgress(false));
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls
                        tableLink="/DailyPlantLogTable"
                        formLink="/DailyPlantLog"
                        addNewLabel="Main Form"
                        enableCancel
                        onCancel={() => navigate('/DailyPlantLogTable')}
                    />
                    <FormPageLayout
                        moduleLabel="MAINTENANCE"
                        title="Daily Plant Log"
                        subtitle="One entry per plant per day — use tabs to enter Plant, EB, equipment hours, breakdown and idle tracking."
                        modeLabel="CREATE MODE"
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary mb-3">
                                        <label className="form-group col-sm-4 text-right">Plant</label>
                                        <select
                                            name="plant_id"
                                            className="form-control col-sm-7 mandatory-form-control"
                                            value={inputs.plant_id}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Plant</option>
                                            {plants.map((p) => (
                                                <option key={p.value} value={p.value}>{p.label}</option>
                                            ))}
                                        </select>

                                        <label className="form-group col-sm-4 text-right">Date</label>
                                        <input
                                            type="date"
                                            name="log_date"
                                            className="form-control col-sm-7"
                                            value={inputs.log_date}
                                            onChange={handleChange}
                                            required
                                        />

                                        <label className="form-group col-sm-4 text-right">Prepared By</label>
                                        <input
                                            name="prepared_by"
                                            className="form-control col-sm-7"
                                            value={inputs.prepared_by}
                                            onChange={handleChange}
                                        />

                                        <label className="form-group col-sm-4 text-right">Remarks</label>
                                        <input
                                            name="user_remarks"
                                            className="form-control col-sm-7"
                                            value={inputs.user_remarks}
                                            onChange={handleChange}
                                        />

                                        <div className="col-sm-12 text-right mt-2">
                                            <Button type="button" color="info" size="sm" onClick={handleAutofill}>
                                                Autofill from DC / Job / Gate
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <MaintenanceAssetSections
                                groupedLines={groupedLines}
                                onGroupedChange={setGroupedLines}
                                equipments={equipments}
                                plantId={inputs.plant_id}
                                logDate={inputs.log_date}
                                cookies={cookies}
                            />

                            <div className="text-right mb-4">
                                <Button color="primary" type="submit">Save Daily Log</Button>
                            </div>
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default DailyPlantLog;
